import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, Scale, ChevronDown } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import axios from 'axios';
import { PLACEHOLDER_IMG, handleImgError } from '@/lib/imagePlaceholder';

const API = window.location.origin + '/api';

function parseWeightRange(label) {
  if (!label) return null;
  const match = label.match(/K(\d+)\s*-\s*K(\d+)/);
  if (!match) return null;
  return { min: parseInt(match[1]), max: parseInt(match[2]) };
}

function generateFineRanges(min, max) {
  const ranges = [];
  for (let s = min; s < max; s++) {
    ranges.push({ label: `K${s} - K${s + 1} worth`, value: s });
  }
  return ranges;
}

function WeightRefiner({ item, onUpdate }) {
  const range = useMemo(() => parseWeightRange(item.weight_label), [item.weight_label]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fineRanges = useMemo(() => range ? generateFineRanges(range.min, range.max) : [], [range]);

  useEffect(() => {
    if (fineRanges.length > 0 && !selected) {
      const current = fineRanges.find(r => r.value === item.weight_price);
      setSelected(current || fineRanges[0]);
    }
  }, [item.weight_price, fineRanges, selected]);

  if (!range || fineRanges.length === 0) {
    return <span className="weight-tag">{item.weight_label || `K${item.weight_price}`}</span>;
  }

  const handleSelect = async (r) => {
    setSelected(r);
    setOpen(false);
    await onUpdate(item.product_id, r.value, r.label);
  };

  return (
    <div className="weight-refiner" data-testid={`weight-refiner-${item.product_id}`}>
      <div className="weight-refiner-label">Specify your exact price:</div>
      <button className="weight-refiner-btn" onClick={() => setOpen(!open)} data-testid={`refine-toggle-${item.product_id}`}>
        <Scale size={13} />
        <span>{selected ? selected.label : item.weight_label}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="weight-refiner-dropdown" data-testid={`refine-dropdown-${item.product_id}`}>
          {fineRanges.map((r) => (
            <button
              key={r.value}
              className={`weight-refiner-option ${selected?.value === r.value ? 'active' : ''}`}
              onClick={() => handleSelect(r)}
              data-testid={`refine-opt-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Cart() {
  const { cartItems, fetchCart, updateQuantity, removeFromCart, getCartTotal, cartCount } = useCart();
  const navigate = useNavigate();

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const handleWeightUpdate = useCallback(async (productId, newPrice, newLabel) => {
    try {
      await axios.put(`${API}/cart/${productId}/weight`, {
        weight_price: newPrice, weight_label: newLabel
      }, { withCredentials: true });
      await fetchCart();
    } catch (error) {
      console.error('Failed to update weight price:', error.message);
    }
  }, [fetchCart]);

  const total = getCartTotal();

  return (
    <div className="cart-page" data-testid="cart-page">
      <h2>Shopping Cart ({cartCount} items)</h2>
      {cartItems.length === 0 ? (
        <div className="empty-state">
          <ShoppingBag size={64} />
          <h3>Your cart is empty</h3>
          <p>Add some products to get started</p>
          <Link to="/" className="btn-lime" style={{ display: 'inline-block', padding: '12px 28px', textDecoration: 'none' }} data-testid="continue-shopping-btn">
            Continue Shopping
          </Link>
        </div>
      ) : (
        <>
          {cartItems.map(item => {
            const product = item.product;
            if (!product) return null;
            const isWeighted = item.weight_price != null;
            const price = isWeighted ? item.weight_price : (
              product.discount_percent > 0 ? product.price * (1 - product.discount_percent / 100) : product.price
            );
            return (
              <div className="cart-item" key={item.product_id} data-testid={`cart-item-${item.product_id}`} style={isWeighted ? { flexWrap: 'wrap' } : {}}>
                <div className="cart-item-img">
                  <img src={product.image_url || PLACEHOLDER_IMG} alt={product.name} onError={handleImgError} />
                </div>
                <div className="cart-item-info" style={{ flex: 1 }}>
                  <h4>
                    {product.name}
                    {isWeighted && <Scale size={12} style={{ marginLeft: '4px', opacity: 0.5, verticalAlign: 'middle' }} />}
                  </h4>
                  <span className="cart-item-price">{price.toFixed(2)} PGK</span>
                  {isWeighted && product.price_per_kg > 0 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{product.price_per_kg.toFixed(2)} PGK/kg</div>
                  )}
                </div>
                {!isWeighted && (
                  <div className="qty-controls">
                    <button className="qty-btn" onClick={() => updateQuantity(item.product_id, Math.max(1, item.quantity - 1))} data-testid={`qty-minus-${item.product_id}`}>
                      <Minus size={14} />
                    </button>
                    <span className="qty-value">{item.quantity}</span>
                    <button className="qty-btn" onClick={() => updateQuantity(item.product_id, item.quantity + 1)} data-testid={`qty-plus-${item.product_id}`}>
                      <Plus size={14} />
                    </button>
                  </div>
                )}
                <button className="remove-btn" onClick={() => removeFromCart(item.product_id)} data-testid={`remove-btn-${item.product_id}`}>
                  <Trash2 size={18} />
                </button>
                {isWeighted && (
                  <div style={{ width: '100%', paddingLeft: '82px', marginTop: '4px' }}>
                    <WeightRefiner item={item} onUpdate={handleWeightUpdate} />
                  </div>
                )}
              </div>
            );
          })}
          <div className="cart-summary" data-testid="cart-summary">
            <div className="cart-summary-row"><span>Subtotal</span><span>{total.toFixed(2)} PGK</span></div>
            <div className="cart-summary-row"><span>Delivery</span><span>Free</span></div>
            <div className="cart-summary-row cart-summary-total"><span>Total</span><span data-testid="cart-total">{total.toFixed(2)} PGK</span></div>
            <button className="btn-lime" style={{ marginTop: '16px' }} onClick={() => navigate('/checkout')} data-testid="checkout-btn">Proceed to Checkout</button>
          </div>
        </>
      )}
    </div>
  );
}
